import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import "./Ecommerce.css";

const Productsubcategory = () => {
  const { categoryId } = useParams();
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch subcategories and products in parallel for better performance
      const [subcategoriesResponse, productsResponse] = await Promise.all([
        fetch(
          `https://vogelkoparchitects.com/api/ecom/subcategories-by-category/${categoryId}/`,
        ),
        fetch(
          `https://vogelkoparchitects.com/api/ecom/products/category/${categoryId}/`,
        ),
      ]);

      // Check for HTTP errors
      if (!subcategoriesResponse.ok || !productsResponse.ok) {
        throw new Error("Failed to fetch data");
      }

      const subcategoriesData = await subcategoriesResponse.json();
      const productsData = await productsResponse.json();

      setSubcategories(subcategoriesData);
      setProducts(productsData.products || []);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load products. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [categoryId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // First group products by subcategory, then by group_code
  const groupedBySubcategory = products.reduce((acc, product) => {
    const subcategoryName = product.subcategory_name || "Uncategorized";
    const groupCode = product.group_code || "Other";

    if (!acc[subcategoryName]) {
      acc[subcategoryName] = {};
    }

    if (!acc[subcategoryName][groupCode]) {
      acc[subcategoryName][groupCode] = [];
    }

    acc[subcategoryName][groupCode].push(product);
    return acc;
  }, {});

  // Loading state
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading products...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button onClick={fetchData} className="retry-button">
          Try Again
        </button>
      </div>
    );
  }

  // Empty state
  if (!subcategories.length && !products.length) {
    return (
      <div className="empty-state">
        <h2>No products found</h2>
        <p>There are no products available in this category.</p>
      </div>
    );
  }

  return (
    <>
      <div className="category-banner">
        <img
          src="../../assets/img/ecommerce/banner/banner1.jpg"
          alt="banner"
          className="banner-image"
        />
        <h1 className="category-title">Products</h1>
      </div>

      <div className="ecommerce-container">
        <div className="products-main-container">
          {Object.keys(groupedBySubcategory).length > 0 ? (
            Object.entries(groupedBySubcategory).map(
              ([subcategoryName, groups]) => (
                <div key={subcategoryName} className="subcategory-section">
                  <h2 className="subcategory-title">{subcategoryName}</h2>

                  {Object.entries(groups).map(
                    ([groupCode, productsInGroup]) => {
                      const useSpecialLayout = productsInGroup.length >= 3;
                      const useRowLayout = productsInGroup.length <= 2;

                      return (
                        <div key={groupCode} className="main-code-section">
                          <h3 className="main-code-title">{groupCode}</h3>

                          {useSpecialLayout && (
                            <div className="special-product-layout">
                              {/* First product - special layout */}
                              <div className="first-product">
                                <Link
                                  to={`/Productbydetail/${productsInGroup[0].id}`}
                                  className="product-link"
                                >
                                  <div className="product-image-container first-product-image">
                                    <img
                                      src={productsInGroup[0].main_image}
                                      alt={productsInGroup[0].product_name}
                                      className="product-image"
                                      onError={(e) => {
                                        e.target.src =
                                          "/path/to/placeholder-image.jpg";
                                      }}
                                    />
                                  </div>
                                  <div className="first-product-details">
                                    <h4 className="product-name">
                                      {productsInGroup[0].product_name}
                                    </h4>
                                    {productsInGroup[0].hint && (
                                      <p className="product-description">
                                        {productsInGroup[0].hint}
                                      </p>
                                    )}
                                  </div>
                                </Link>
                              </div>

                              {/* Remaining products in grid */}
                              <div className="remaining-products-grid">
                                {productsInGroup.slice(1).map((product) => (
                                  <Link
                                    to={`/Productbydetail/${product.id}`}
                                    key={product.id}
                                    className="product-item"
                                  >
                                    <div className="product-image-container">
                                      <img
                                        src={product.main_image}
                                        alt={product.product_name}
                                        className="product-image"
                                        onError={(e) => {
                                          e.target.src =
                                            "/path/to/placeholder-image.jpg";
                                        }}
                                      />
                                    </div>
                                    <div className="product-details">
                                      <h4 className="product-name">
                                        {product.product_name}
                                      </h4>
                                      {product.hint && (
                                        <p className="product-code">
                                          {product.hint}
                                        </p>
                                      )}
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          )}

                          {useRowLayout && (
                            <div className="two-products-row">
                              {productsInGroup.map((product) => (
                                <Link
                                  to={`/Productbydetail/${product.id}`}
                                  key={product.id}
                                  className="product-item"
                                >
                                  <div className="product-image-container">
                                    <img
                                      src={product.main_image}
                                      alt={product.product_name}
                                      className="product-image"
                                      onError={(e) => {
                                        e.target.src =
                                          "/path/to/placeholder-image.jpg";
                                      }}
                                    />
                                  </div>
                                  <div className="product-details">
                                    <h4 className="product-name">
                                      {product.product_name}
                                    </h4>
                                    {product.hint && (
                                      <p className="product-code">
                                        {product.hint}
                                      </p>
                                    )}
                                  </div>
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    },
                  )}
                </div>
              ),
            )
          ) : (
            <div className="no-products-message">
              <p>No products available.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Productsubcategory;
